import React, { useState, useEffect, useRef } from 'react'
import styles from './ClickActions.scss'
import { icons } from '../icons'
import { plannerMessages } from '../intlMessages'
import { Icon as LegacyIcon } from 'common/LegacyIcon'
import { PopoverMenu, PopoverMenuItem, PopoverMenuHeading } from 'common/styleguide/PopoverMenu'
import { Popover } from 'common/styleguide/Popover'
import { commonNouns, commonVerbs, useFormatMessage } from 'lib/localization'
import cx from 'classnames'
import { FormattedMessage as T } from 'react-intl'
import { experimentEnabled } from 'lib/utils'

const R = window.Routes

export const colors = ['#FF0000', '#00FF00', '#0000FF', '#FFFF00', '#000000', '#FF00FF', '#802A2A']

export const ClickActions = ({
  needsUpgrade,
  optimizeForDefault,
  selectedCommand,
  optimizeForCallback,
  avoidHighwaysChanged,
  setCallbacks = {},
  unsetCallbacks = {}
}) => {
  const fm = useFormatMessage()
  const isGoogleMap = ['roadmap', 'satellite', 'hybrid', 'terrain'].includes(R.activeMap.gmap.getMapTypeId())

  const [command, setCommand] = useState(selectedCommand)
  useEffect(() => {
    setCallbacks[command] && setCallbacks[command]()
    R.New.mapCommand.selectedCommand = command
    return () => {
      unsetCallbacks[command] && unsetCallbacks[command]()
      delete R.New.mapCommand.selectedCommand
    }
  }, [command])

  // Even when command is not followRoads or drawLines, we want to remember which was selected so we can switch back to the correct one
  const drawLines = useRef(false)
  if (command === 'followRoads') drawLines.current = false
  if (command === 'drawLines') drawLines.current = true

  const [optimizeFor, setOptimizeFor] = useState(optimizeForDefault ?? 'cycling')
  useEffect(() => {
    if (isGoogleMap && optimizeFor === 'experiment') {
      setOptimizeFor('cycling')
    } else {
      optimizeForCallback && optimizeForCallback(optimizeFor)
    }
  }, [optimizeFor])

  const [avoidHighways, setAvoidHighways] = useState(true)
  useEffect(() => {
    avoidHighwaysChanged && avoidHighwaysChanged('avoidHighways', avoidHighways)
  }, [avoidHighways])

  // Faciliate hotkey support, which is still managed from outside React
  useEffect(() => {
    window.rwgps.Sidebar = {
      selectCommand: command => setCommand(command)
    }
    return () => { delete window.rwgps.Sidebar }
  }, [])

  const [lineColor, setLineColor] = useState(colors[0])
  useEffect(() => {
    R.activeMap.setColor('0x' + lineColor.split('#')[1], true)
  }, [lineColor])

  const [routingExpanded, setRoutingExpanded] = useState(false)
  const routingModeRef = useRef()
  const [colorExpanded, setColorExpanded] = useState(false)
  const colorRef = useRef()

  return <>
    <div className={styles.ClickActions}>
      <div className={styles.title}><T id='ClickActions.clickAction' defaultMessage='Click Action' /></div>
      <div className={styles.boxes}>
        <div className={cx((command === 'followRoads' || command === 'drawLines') && styles.selected)} onClick={() => setCommand(drawLines.current ? 'drawLines' : 'followRoads')}>
          {icons.addToRoute}
          <div><T id='ClickActions.addToRoute' defaultMessage='Add to Route' /></div>
        </div>
        <div className={cx(command === 'createPoi' && styles.selected, needsUpgrade && styles.needsUpgrade)} onClick={() => setCommand('createPoi')}>
          {icons.dropPoi}
          <div><T id='ClickActions.dropPoi' defaultMessage='Drop POI' /></div>
        </div>
        <div className={cx(command === 'addCue' && styles.selected, needsUpgrade && styles.needsUpgrade)} onClick={() => setCommand('addCue')}>
          {icons.customCue}
          <div><T id='ClickActions.customCue' defaultMessage='Custom Cue' /></div>
        </div>
        <div className={cx(command === 'createControlPoint' && styles.selected)} onClick={() => setCommand('createControlPoint')}>
          {icons.controlPoint}
          <div><T id='ClickActions.controlPoint' defaultMessage='Control Point' /></div>
        </div>
      </div>
    </div>
    <div className={cx(styles.routingAndColor, !(command === 'followRoads' || command === 'drawLines') && styles.notRouting)}>
      <div>
        <div className={styles.title}><T id='ClickActions.routing' defaultMessage='Routing' /></div>
        <div className={styles.boxes}>
          <div ref={routingModeRef} title={fm(commonNouns[optimizeFor])} className={cx(styles.routing, !drawLines.current && styles.selected)} onClick={() => {
            if (command === 'followRoads') {
              setRoutingExpanded(!routingExpanded)
            }
            setCommand('followRoads')
          }}>
            <span style={{ marginRight: drawLines.current ? 0 : '10px' }}>{drawLines.current ? fm(commonVerbs.followRoads) : fm(commonNouns[optimizeFor])}</span>
            {routingExpanded
              ? <LegacyIcon i='upChevron' size={20} className={styles.chevron} />
              : <LegacyIcon i='downChevron' size={20} className={styles.chevron} />}
          </div>
          {routingExpanded && <PopoverMenu className={styles.optimizeForMenu} target={routingModeRef.current} placement='bottom' onRequestClose={() => setRoutingExpanded(false)}>
            <PopoverMenuItem onClick={() => { setOptimizeFor('cycling'); setRoutingExpanded(false) }} className={optimizeFor === 'cycling' && styles.selectedRoutingMode}>
              {icons.cycling}
              <span>{fm(commonNouns.cycling)}</span>
            </PopoverMenuItem>
            <PopoverMenuItem onClick={() => { setOptimizeFor('driving'); setRoutingExpanded(false) }} className={optimizeFor === 'driving' && styles.selectedRoutingMode}>
              {icons.driving}
              <span>{fm(commonNouns.driving)}</span>
            </PopoverMenuItem>
            <PopoverMenuItem onClick={() => { setOptimizeFor('walking'); setRoutingExpanded(false) }} className={optimizeFor === 'walking' && styles.selectedRoutingMode}>
              {icons.walking}
              <span>{fm(commonNouns.walking)}</span>
            </PopoverMenuItem>
            {experimentEnabled('routing') && !isGoogleMap && <PopoverMenuItem onClick={() => { setOptimizeFor('experiment'); setRoutingExpanded(false) }} className={optimizeFor === 'experiment' && styles.selectedRoutingMode}>
              {icons.cycling}
              <span>{fm(commonNouns.experiment)}</span>
            </PopoverMenuItem>}
            {isGoogleMap && <>
              <PopoverMenuHeading>{fm(commonNouns.options)}</PopoverMenuHeading>
              <PopoverMenuItem>
                <label style={{ fontWeight: 500 }}>
                  <input type='checkbox' onChange={e => setAvoidHighways(e.currentTarget.checked)} checked={avoidHighways} />
                  {fm(plannerMessages.avoidHighways)}
                </label>
              </PopoverMenuItem>
            </>}
          </PopoverMenu>}
          <div className={cx(styles.drawLines, drawLines.current && styles.selected)} onClick={() => setCommand('drawLines')}>
            <T id='ClickActions.drawLines' defaultMessage='Draw Lines' />
          </div>
          <div ref={colorRef} className={styles.color} onClick={() => setColorExpanded(!colorExpanded)}>
            <div style={{ backgroundColor: lineColor, border: (lineColor === '#FFFF00' && '1px solid #BDBDBD') }} />
          </div>
          {colorExpanded && <Popover noPadding target={colorRef.current} placement='bottom' onRequestClose={() => setColorExpanded(false)}>
            <div className={styles.swatches}>
              {colors.map(color => (
                <div onClick={() => { setLineColor(color); setColorExpanded(false) }} className={cx(styles.swatch, color === lineColor && styles.selected)}>
                  <div style={{ backgroundColor: color, border: (color === '#FFFF00' && '1px solid #BDBDBD') }} />
                </div>
              ))}
            </div>
          </Popover>}
        </div>
      </div>
    </div>
  </>
}
